/**
 * Analytics values shared by the show carousel and its media modal.
 *
 * Both the card (GTM auto-capture attributes) and the modal (explicit DDS events)
 * have to agree on the same position / destination strings, so they are built here
 * rather than hand-rolled in each file. Positions and the modal's link names go through
 * analyticsText; the card's link name does NOT — reportShowCardKeyboardClick below trims and
 * lower cases only, and getCardLinkName in show-carousal.tsx lower cases without trimming —
 * so a title carrying a newline reports a linkName that does not match the linkPosition built
 * from the same title. Both of those are AC1 call sites and stay as they are here, pending a
 * separate change. Destinations are built three different ways: SHOW_CAROUSEL_CARD_DESTINATION is a
 * fixed relative path; getCurrentPageUrl and getShowModalPageUrl build against the current origin;
 * absoluteAnalyticsUrl uses that origin only as a base for a relative input, and passes anything
 * unresolvable through as it came. All of them keep the path's case.
 *
 * The modal's three events (AC2/AC3/AC4) are authored on the ticket as raw
 * `dataLayer.push` calls with event names no DDS helper produces. They go out through
 * the service's fireDataLayerEvent instead, which pushes the SAME object: a raw push
 * here would skip the parent-window handling the Storyblok visual editor needs (the
 * editor renders the page in an iframe, so an un-forwarded push lands on a dataLayer no
 * GTM container is listening to), and it would skip the app's post-paint analytics
 * queue, which a bare import of the service resolves to (the exact-match webpack alias
 * in next.config.js, mirrored by jest's moduleNameMapper).
 */
import {
  fireCustomLinkClickGA,
  fireDataLayerEvent
} from "@directv-digital/dds.services.analytics-service";

export const SHOW_CAROUSEL_CARD_POSITION = "panel_show carousel";

/**
 * Destination reported for a card click. Governance describes modal destinations as
 * `/modal/[identifier]`; this component reports the form the analytics team keyed
 * GA reporting to for the show carousel.
 */
export const SHOW_CAROUSEL_CARD_DESTINATION = "/modal-show_carousel";

const SHOW_MODAL_POSITION = "modal_show carousel";

const MODAL_PAGE_URL_SUFFIX = "/show_carousel";

// Reported names and positions are lower case, with interior whitespace collapsed: they are
// CMS/OV strings, and a stray newline or double space splits what should be one dimension
// value into several rows in the report. A multi-line show name is not hypothetical — the
// modal renders the heading with whiteSpace: "pre-wrap" precisely so it can carry one.
// A non-string must not throw: the heading reaches us from an untyped payload, and this runs
// during render, ahead of the modal's hooks. URLs deliberately do NOT go through this — see
// absoluteAnalyticsUrl.
const analyticsText = (value?: string): string => (
  typeof value === "string" ? value.replace(/\s+/g, " ").trim().toLowerCase() : ""
);

/**
 * `modal_show carousel_yellowstone` — the position reported for every interaction
 * inside the show modal. Falls back to the bare modal identifier when the show name
 * is not available, so the value is never blank.
 */
export const getShowModalPosition = (showName?: string): string => {
  const name = analyticsText(showName);
  return name ? `${SHOW_MODAL_POSITION}_${name}` : SHOW_MODAL_POSITION;
};

/**
 * The page behind the modal — origin and pathname. Closing the modal does not navigate, so
 * this is what AC4 reports as the destination. Empty on the server.
 *
 * Query string and hash are dropped: a genre-pack url can carry a zip or a campaign
 * identifier, and neither belongs in an analytics dimension. That leaves this reporting the
 * same page as the modal's own virtual url (getShowModalPageUrl, which drops them too) and
 * follows the reporting service, which sets `page.location.url` from window.location.pathname
 * (lib/service/reporting-service/reporting-service.ts:426 and :442) and reports the full href
 * on the sitemap page only, under the separately named `page.location._addressBarFullURL`
 * (:440). Case survives, for the reason spelled out on absoluteAnalyticsUrl.
 */
export const getCurrentPageUrl = (): string => {
  if (typeof window === "undefined") {
    return "";
  }
  return `${window.location.origin}${window.location.pathname}`;
};

/**
 * Current page URL with the modal suffix appended, used as the modal's page view URL.
 * Returns an empty string on the server so nothing is reported before hydration.
 *
 * The path is passed through with its case intact, for the reason spelled out on
 * absoluteAnalyticsUrl: a lower cased url can name a page that does not resolve. It also keeps
 * AC2 and AC4 reporting the one page under one casing — getCurrentPageUrl keeps the path's
 * case too, so lower casing here would split a mixed-case path across two values.
 */
export const getShowModalPageUrl = (): string => {
  if (typeof window === "undefined") {
    return "";
  }
  const pageUrl = `${window.location.origin}${window.location.pathname}`.replace(
    /\/+$/,
    ""
  );
  return `${pageUrl}${MODAL_PAGE_URL_SUFFIX}`;
};

/**
 * Reports a card click the GTM click capture cannot see. Activating a role="button" card
 * from the keyboard fires no DOM click, so the card's data attributes are never read —
 * call this from keydown handlers only, or a mouse click is counted twice.
 */
export const reportShowCardKeyboardClick = (showName?: string): void => {
  const linkName =
    typeof showName === "string" ? showName.trim().toLowerCase() : "";
  if (!linkName) {
    return;
  }
  fireCustomLinkClickGA({
    event: "trackEvent",
    event_name: "custom_link_click",
    linkName,
    linkPosition: SHOW_CAROUSEL_CARD_POSITION,
    linkDestination: SHOW_CAROUSEL_CARD_DESTINATION
  });
};

/**
 * Absolute form of a destination, case intact. Relative targets (the tablet/mobile guide
 * fallback) resolve against the current origin.
 *
 * Case must survive: AC3 reports the watch destination, and its OV path segment
 * `VIDEO_CONTENT` and the resourceId that follows it are case-bearing, so a lower cased
 * value names a page that does not resolve. `new URL` rather than a hand-rolled scheme
 * test and string concatenation, because the latter mis-handles a protocol-relative
 * `//host/path` — it would resolve against our origin and report the wrong place.
 */
export const absoluteAnalyticsUrl = (url?: string): string => {
  if (!url) {
    return "";
  }
  if (typeof window === "undefined") {
    return url;
  }
  try {
    return new URL(url, window.location.origin).href;
  } catch (_error) {
    // Not resolvable (a malformed CMS value): report it verbatim rather than dropping the
    // event — a wrong-looking destination is easier to trace than a missing one.
    return url;
  }
};

// Both emitters are a no-op on the server. fireDataLayerEvent dereferences `window`
// unguarded, and the webpack alias that routes this import through the app's post-paint
// queue is client-bundles-only — so on the server this import IS the raw DDS function
// and an unguarded call would throw during SSR.
const push = (payload: Record<string, unknown>): void => {
  if (typeof window === "undefined") {
    return;
  }
  fireDataLayerEvent(payload);
};

export type ShowModalLinkClickArgs = {
  linkName?: string;
  linkPosition: string;
  linkDestination: string;
};

/**
 * AC2 — the modal's own page view. No linkName: the AC defines none. `event` is
 * `page_view` rather than the `trackEvent` the rest of this component uses, which is the
 * AC's own wording.
 */
export const fireShowModalPageView = (showName?: string): void => push({
  event: "page_view",
  event_name: "page_view",
  linkPosition: getShowModalPosition(showName),
  linkDestination: getShowModalPageUrl()
});

/**
 * AC3 (watch now) / AC4 (close). `event: "link_click"` is the AC's own value, not the
 * `trackEvent` trigger key the DDS helpers emit.
 *
 * linkName arrives from the CMS on the AC3 path, so it goes through analyticsText, and a
 * label left blank reports nothing at all rather than an event whose name dimension is empty
 * and unattributable.
 */
export const fireShowModalLinkClick = ({
  linkName,
  linkPosition,
  linkDestination
}: ShowModalLinkClickArgs): void => {
  const name = analyticsText(linkName);
  if (!name) {
    return;
  }
  push({
    event: "link_click",
    event_name: "link_click",
    linkName: name,
    linkPosition,
    linkDestination
  });
};
